import { CompositeItem } from '@floating-ui/react';
import classNames from 'classnames';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type HTMLAttributes,
  type JSX,
} from 'react';
import { useDiveContext } from '../../../context';
import { omitProps } from '../../../helpers/helpers';
import { Button } from '../../Button/Button';
import type { ButtonProps } from '../../Button/Button.types';
import type { DiNavItemProps } from './DiNavItem.types';

/**
 * Internal `DiNavItem` component, serving as the foundation for `TabListItem` and `HeaderNavItem`.
 * Designed to be used within a `<DiNav />` component.
 * Utilizes {@link https://floating-ui.com/docs/composite Floating UI's CompositeItem}
 * to enable arrow-key navigation in non-floating contexts when placed inside a `<Composite>`.
 *
 * **Note**: Currently, `DiNavItem` and `DiNav` are not directly connected.
 * There is no built-in synchronization mechanism (e.g., via context or state) to track which item is selected.
 * Instead, the responsibility for managing selection state and synchronization is delegated to a higher-level component,
 * such as `HeaderNav` or `Tabs`, which can implement the necessary logic based on the application's needs.
 *
 * @example
 *
 * ```tsx
 * <DiNavItem label="Movie" icon={<MovieIcon />} />
 * <DiNavItem label="Movie" icon={<MovieIcon />} isCurrent /> // Current page
 * <DiNavItem label="Movie" icon={<MovieIcon />} href="/home" /> // Link
 * <DiNavItem label="Movie" icon={<MovieIcon />} wrapper={<Link href="/movie" />} /> // Custom wrapper
 * ```
 */
export const DiNavItem = forwardRef<HTMLElement, DiNavItemProps>(
  function DiNavItem(
    {
      outerWrapper,
      buttonClassName,
      font = 'hind',
      href,
      icon,
      iconClassName,
      iconPosition = 'left',
      id: propsId,
      label,
      labelClassName,
      role = 'menuitem',
      tabIndex,
      wrapper,
      className,
      // a11y and floating-ui HTML attributes
      ...props
    },
    forwardedRef,
  ): JSX.Element | undefined {
    const { isTv } = useDiveContext();
    const uniqueId = useId();
    const id = propsId ?? uniqueId;

    const commonProps: HTMLAttributes<HTMLElement> = {
      id,
      role,
      // omit non-native HTML attributes
      ...omitProps(props, ['isCurrent', 'isFocused']),
    };

    const renderWrapper: ButtonProps['renderWrapper'] = ({
      children,
      className,
    }) => (
      <CompositeItem
        className={className}
        ref={forwardedRef}
        render={(compositeItemProps) => {
          if (href) {
            return (
              <a
                href={href}
                {...commonProps}
                {...compositeItemProps}
                tabIndex={tabIndex}
              >
                {children}
              </a>
            );
          }
          if (wrapper) {
            return cloneElement(wrapper, {
              ...commonProps,
              ...compositeItemProps,
              tabIndex,
              children,
            });
          }
          return (
            <button
              type="button"
              {...commonProps}
              {...compositeItemProps}
              tabIndex={tabIndex}
            >
              {children}
            </button>
          );
        }}
      />
    );

    const navItemButton = (
      <Button
        id={id}
        variant={isTv ? 'tertiary' : 'text'}
        font={font}
        iconPosition={iconPosition}
        className={classNames(buttonClassName, className)}
        renderWrapper={renderWrapper}
        {...(icon && {
          icon: cloneElement(icon, {
            className: iconClassName,
          }),
        })}
      >
        <span className={labelClassName}>{label}</span>
      </Button>
    );

    if (isValidElement(outerWrapper)) {
      return cloneElement(outerWrapper, {
        children: navItemButton,
      });
    }

    return navItemButton;
  },
);
